<script>
export default {
  props: {
    data1: Array,
    columns: Array,
    columnsDisplayed: Object,
    filterKey: String,
    supplierFilter: String,
    perPage: Number,
    fetchDone: Boolean
  },
  data() {
    return {
      sortKey: '',
      sortOrders: this.columns.reduce((o, key) => ((o[key] = 1), o), {}),
      page: 1,
      pages: [],
      data: [],
      showPreview: false,
      previewInvoiceId: null,
      previewLoading: false
    }
  },

  computed: {
    displayedData() {
      return this.paginate(this.filteredData(this.data1));
    },
    displayedDataAll() {
      return this.filteredData(this.data1);
    }
  },

  watch: {
    displayedData() {
      this.setPages();
    }
  },
  methods: {
    onImageLoadFailure(event) {
      event.target.src = 'https://cdn1.iconfinder.com/data/icons/unicons-line-vol-2/24/cloud-question-512.png'
    },
    sortBy(key) {
      this.sortKey = key
      this.sortOrders[key] = this.sortOrders[key] * -1
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    setPages() {
      this.pages = [];
      let numberOfPages = Math.ceil(this.filteredData(this.data1).length / this.perPage);
      for (let index = 1; index <= numberOfPages; index++) {
        this.pages.push(index);
      }
    },
    filteredData() {
      const sortKey = this.sortKey
      const filterKey = this.filterKey && this.filterKey.toLowerCase()
      const supplierFilter = this.supplierFilter
      const order = this.sortOrders[sortKey] || 1
      let data = this.data1

      // Filtre par fournisseur
      if (supplierFilter) {
        data = data.filter((row) => {
          return row.company_name === supplierFilter
        })
      }

      // Filtre par recherche texte
      if (filterKey) {
        data = data.filter((row) => {
          return Object.keys(row).some((key) => {
            return String(row[key]).toLowerCase().indexOf(filterKey) > -1
          })
        })
      }

      // Tri
      if (sortKey) {
        data = data.slice().sort((a, b) => {
          a = a[sortKey]
          b = b[sortKey]
          return (a === b ? 0 : a > b ? 1 : -1) * order
        })
      }
      return data
    },
    paginate(data) {
      let page = this.page;
      let perPage = this.perPage;
      let from = (page * perPage) - perPage;
      let to = (page * perPage);
      return data.slice(from, to);
    },
    openPreview(invoiceId) {
      this.previewLoading = true;
      this.previewInvoiceId = invoiceId;
      this.showPreview = true;
      
      // Simuler un délai de chargement pour une meilleure UX
      setTimeout(() => {
        this.previewLoading = false;
      }, 500);
    },
    closePreview() {
      this.showPreview = false;
      this.previewInvoiceId = null;
      this.previewLoading = false;
    },
    navigateToInvoice(invoiceId, event) {
      // Empêcher la navigation si on clique sur un bouton ou un lien
      if (event.target.closest('button') || event.target.closest('a')) {
        return;
      }
      this.$router.push(`/invoice/${invoiceId}`);
    }
  }
}
</script>

<script setup>
import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/vue/solid'
import {ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon} from '@heroicons/vue/solid'


</script>

<template>

  <div v-if="fetchDone && displayedData.length > 0">
    <table
        class="min-w-full divide-y divide-gray-300"
    >
      <thead class="bg-gray-50">
      <tr>
        <th
            v-for="(item, index) in columnsDisplayed"
            :key="index"
            :class="{ active: sortKey == index }"
            class="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
            @click="sortBy(index)"
        >
          <a
              href="#"
              class="group inline-flex"
          >
            {{ capitalize(item) }}
            <span class="ml-1.5 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300">
              <ChevronDownIcon
                  v-if="sortOrders[index] > 0"
                  class="h-4 w-4"
                  aria-hidden="true"
              />
              <ChevronUpIcon
                  v-if="sortOrders[index] < 0"
                  class="h-4 w-4"
                  aria-hidden="true"
              />

            </span>
          </a>
          <span
              class="arrow"
              :class="sortOrders[index] > 0 ? 'asc' : 'dsc'"
          />
        </th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 bg-white">
      <tr
          v-for="entry in displayedData"
          :key="entry"
          :class="[
            entry.isOutOfFiscalYear ? 'bg-yellow-50' : '',
            entry.isOutOfCurrentFiscalYear ? 'bg-gray-100 opacity-60' : '',
            'cursor-pointer hover:bg-gray-50'
          ]"
          @click="navigateToInvoice(entry['button'], $event)"
      >

        <td
            v-for="(item, index) in columnsDisplayed"
            :key="index"
            class="overflow-hidden  whitespace-wrap px-2 py-1 text-sm text-gray-500"
        >
          <span class="hidden bg-yellow-700 bg-green-700 bg-red-700  bg-fuchsia-700  bg-gray-700"></span>
          <div class="flex items-center" v-if="index === 'company_name'">
            <div class="h-6 w-6 flex-shrink-0">
              <img @error="onImageLoadFailure($event)" class="h-6 w-6 rounded-full" :src="entry['company_avatar']"
                   alt=""/>
            </div>
            <div class="ml-2.5 min-w-0">
              <div class="flex items-center flex-wrap gap-1">
                <div class="truncate font-medium text-gray-900">{{ entry['company_name'] }}</div>
                <span v-if="entry['invoice_id'] != null" class="text-xs text-gray-400">{{ entry['invoice_id'] }}</span>
                <span v-if="entry.isOutOfFiscalYear" class="inline-flex items-center rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800" title="Date hors exercice fiscal">
                  <svg class="mr-0.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                  Hors exercice
                </span>
                <span v-if="entry.transactionCount" :title="`${entry.transactionCount} transaction(s) associée(s) pour ${entry.transactionAmount} €`"
                      :class="['inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
                               entry.transactionIsCovered ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800']">
                  <svg class="mr-0.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"/>
                  </svg>
                  {{ entry.transactionCount > 1 ? entry.transactionCount + ' transactions' : 'Transaction' }}
                  <span v-if="!entry.transactionIsCovered" class="ml-0.5">· {{ entry.transactionAmount }} €</span>
                </span>
                <span v-if="entry.isOld && entry.status_name && entry.status_name.toLowerCase().includes('attente')" class="inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800" title="Ancienne (>30 jours)">
                  <svg class="mr-0.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
                  </svg>
                  Urgent
                </span>
              </div>
            </div>
          </div>


          <span v-else-if="index === 'status_name'"
                :class="`inline-flex rounded-full bg-${entry['status_color']}-700  px-2 text-xs font-semibold leading-5 text-white`">
            {{ entry['status_name'] }}
          </span>

          <div v-else-if="index === 'amount'" class="whitespace-nowrap">
            <span class="text-gray-900">{{ entry['amount'] }}</span>
            <span v-if="entry['amount_tax']" class="ml-1 text-xs text-gray-500">({{ entry['amount_tax'] }})</span>
          </div>

          <div v-else-if="index === 'billing_account'" class="max-w-[200px] whitespace-nowrap text-sm" :title="entry['billing_account_name']">
            <span class="font-medium text-gray-900">{{ entry['billing_account'] }}</span>
            <span class="ml-1 text-xs text-gray-500">{{ entry['billing_account_name'] }}</span>
          </div>

          <div v-else-if="index === 'button'">
            <div class="flex space-x-1.5">
              <button type="button"
                      @click="openPreview(entry['button'])"
                      class="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Aperçu
              </button>
              <router-link :to="'/invoice/' + entry['button']">
                <button type="button"
                        class="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  Visualiser
                </button>
              </router-link>
            </div>
          </div>

          <div v-else-if="index === 'date' || index === 'created_at'" class="min-w-[92px] whitespace-nowrap">
            {{ entry[index] }}
          </div>

          <div v-else-if="index === 'comment'" class="max-w-[160px] truncate" :title="entry['comment']">
            {{ entry['comment'] }}
          </div>

          <div v-else>
            {{ entry[index] }}
          </div>

        </td>
      </tr>
      </tbody>
    </table>

    <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div class="flex-1 flex justify-between sm:hidden">
        <a href="#" v-if="page != 1" @click="page--"
           class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          Previous </a>
        <a href="#" @click="page++" v-if="page < pages.length"
           class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          Next </a>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            {{ ' ' }}
            <span class="font-medium">{{ page }}</span>
            {{ ' ' }}
            to
            {{ ' ' }}
            <span class="font-medium">{{ pages.length }}</span>
            {{ ' ' }}
            of
            {{ ' ' }}
            <span class="font-medium">{{ displayedDataAll.length }}</span>
            {{ ' ' }}
            results
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <a href="#" @click="page = 1"
               class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">First</span>
              <ChevronDoubleLeftIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
            <a href="#" v-if="page != 1" @click="page--"
               class="relative inline-flex items-center px-2 py-2  border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Previous</span>
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
            <!-- Current: "z-10 bg-indigo-50 border-indigo-500 text-indigo-600", Default: "bg-white border-gray-300 text-gray-500 hover:bg-gray-50" -->
            <a href="#" aria-current="page" v-bind:key="pageNumber" type="button"
               v-for="pageNumber in pages.slice(page-1, page+5)" @click="page = pageNumber"
               :class="[ pageNumber === page ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium': 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium']">
              {{ pageNumber }}</a>

            <a href="#" @click="page++" v-if="page < pages.length"
               class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Next</span>
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true"/>
            </a>

            <a href="#" @click="page = pages.length"
               class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Last</span>
              <ChevronDoubleRightIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
          </nav>
        </div>
      </div>
    </div>
  </div>


  <div
      v-else-if="fetchDone === false"
      class="bg-white"
  >
    <div class="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8 ">
      <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
        <div
            class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status">
  <span
      class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]"
  >Loading...</span
  >
        </div>
        <span class="block"></span>
      </h2>

    </div>
  </div>

  <div
      v-else-if="displayedData.length === 0"
      class="bg-white"
  >
    <div class="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
      <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
        <span v-if="filterKey" class="block">Aucun résultat pour « {{ filterKey }} »</span>
        <span v-else class="block">Il n'y a aucune facture ici !</span>
        <span class="block"></span>
      </h2>

    </div>
  </div>

  <!-- Modal de prévisualisation - Panneau latéral droit -->
  <div v-if="showPreview" class="fixed inset-0 z-50 overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="absolute inset-0 overflow-hidden">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closePreview"></div>
      
      <section class="absolute inset-y-0 right-0 pl-10 max-w-full flex" aria-labelledby="slide-over-heading">
        <div class="w-screen max-w-2xl">
          <div class="h-screen flex flex-col bg-white shadow-xl transform transition ease-in-out duration-300 translate-x-0">
            <div class="flex-1 flex flex-col">
              <!-- Header -->
              <div class="px-4 py-6 bg-gray-50 sm:px-6">
                <div class="flex items-center justify-between">
                  <h2 class="text-lg font-medium text-gray-900" id="slide-over-heading">
                    Aperçu de la facture
                  </h2>
                  <div class="ml-3 h-7 flex items-center">
                    <button type="button" @click="closePreview" class="bg-gray-50 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <span class="sr-only">Fermer</span>
                      <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Body -->
              <div class="flex-1 px-4 py-6 sm:px-6 overflow-hidden">
                <div v-if="previewLoading" class="flex justify-center items-center h-96">
                  <div class="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
                </div>
                
                <div v-else-if="previewInvoiceId" class="w-full" style="height: calc(100vh - 220px);">
                  <embed :src="`https://billing.clafoutis.eu/api/file/${previewInvoiceId}#toolbar=0&navpanes=0&`"
                         width="100%"
                         height="100%"
                         type="application/pdf"
                         class="rounded border border-gray-300"/>
                </div>
              </div>
              
              <!-- Footer -->
              <div class="flex-shrink-0 px-4 py-4 flex justify-end space-x-2 bg-gray-50 border-t border-gray-200 relative z-10">
                <button type="button" @click="closePreview" class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  Fermer
                </button>
                <router-link :to="'/invoice/' + previewInvoiceId" v-if="previewInvoiceId">
                  <button type="button" class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Voir détails
                  </button>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>


</template>
